/**
 * siteInit is an initialization script for setting up client-side
 * infrastructure used by the whole website.
 *
 * This should be run in every entrypoint before we mount the app.
 */
import type {SupportedThemes} from "@khanacademy/wonder-blocks-theming";
import {GoogleAnalytics} from "@khan/analytics-google";
import * as LearningTimeHeartbeat from "@khan/analytics-learning-time/heartbeat";
import {PageLifecycle, trackWebVitals} from "@khan/analytics-performance";
import {linguiInit} from "@khan/i18n";
import {initKhanSentry, type Integration} from "@khan/logging";
import {hasDevFlag, InitialRequestUrl, RequestInfo} from "@khan/shared-core";

import {init as A11yInit} from "./a11y.ts";
import {loadCookieConsent} from "./cookie-consent.ts";
import * as CookieRefresh from "./cookie-refresh.ts";
import joinUs from "./join-us.ts";
import {shouldInitAnalytics} from "./should-init-analytics.ts";

interface SiteInitOptions {
    sentryNoSample?: boolean;
    sentryIntegrations?: Integration[];
    sentryDsn: string;

    /**
     * Used for frontends that are loaded in a context without access to cookies.
     *
     * WARNING: This is only for use in extraordinary contexts where cookies are
     * forbidden by the CSP, such as mcp-embed.
     */
    noCookies?: boolean;

    /**
     * Used to set a Wonder Blocks theme on the body, to ensure that the theme
     * is applied to the entire experience.
     */
    wbTheme?: SupportedThemes;
}

/**
 * Initialize site-wide infrastructure.
 */
export async function siteInit(options: SiteInitOptions) {
    // eslint-disable-next-line no-restricted-syntax -- we need access to the search params
    const {pathname, search} = window.location;

    if (!options.noCookies) {
        loadCookieConsent();
    }

    // Configure Sentry or set up our own error handler.
    if (shouldInitAnalytics()) {
        initKhanSentry({
            enabled: true,
            // An optional override for testing Sentry error-logging and tracing.
            // Will force Sentry to no sample the requests and will enable it for all
            // requests instead.
            forceNoSample:
                options?.sentryNoSample ?? hasDevFlag("noSentrySample"),
            integrations: options.sentryIntegrations,
            dsn: options.sentryDsn,
        });

        PageLifecycle.init(pathname, search);
        GoogleAnalytics.init();
    }

    // We set up tracking of web vitals here because we want to ensure
    // that we're able to log these events later as part of
    // page-performance.js.
    trackWebVitals();

    // Display recruiting message in the browser console.
    joinUs();

    A11yInit();

    // Initialize Lingui
    linguiInit();

    if (!options.noCookies) {
        LearningTimeHeartbeat.init();
        CookieRefresh.init();
    }

    // If a Wonder Blocks theme is specified then we need to set it on the body,
    // to ensure that the theme is applied to the entire experience.
    const customTheme = InitialRequestUrl.configParams.theme || options.wbTheme;
    if (customTheme) {
        document.body.dataset.wbTheme = customTheme;
    }

    if (RequestInfo.kaLocale === "en-pt") {
        const {JiptInterface} = await import("@khan/translate");
        JiptInterface.init();
    }
}
